<template>
    <div id="print-delivery-component">
        <spinner-overlay v-if="loading" />
        <print-container v-else :title="`Køreplan ${deliveryDate.format('DD-MM-YYYY')}`"
            :header_style="{ display: 'none' }">
            <div class="container" v-for="(trip, index) in trips"
                :class="{ 'print-break-after': trips.length - 1 !== index }" :key="index">
                <table class="trip-table">
                    <!-- The banner lives in thead so the browser repeats it at the top
                         of every printed page, in flow. As a position:fixed bar it was
                         painted over the first row of any continuation page. -->
                    <thead>
                        <tr>
                            <th>
                                <div class="h3 title-header">KØREPLAN - {{ deliveryDate.format('dddd DD-MM-YYYY') }}</div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <div class="my-5">
                                    <div class="row justify-content-around align-items-center">
                                        <div class="col-6 delivery-header">
                                            <div class="row justify-content-center">
                                                <div class="col-4 text-align-end">NAVN:</div>
                                                <div class="col-4 px-2">{{ trip.driverName }}</div>
                                            </div>
                                            <div class="row justify-content-center">
                                                <div class="col-4 text-align-end">BIL:</div>
                                                <div class="col-4 px-2">{{ trip.vehicleName }}</div>
                                            </div>
                                            <!-- AFGANG shares this line with PAK BIL to hold the block
                                                 to three lines. It rides inside the same value column so
                                                 the PAK BIL label stays aligned with NAVN and BIL above;
                                                 nowrap lets it run into the empty space to the right. -->
                                            <div class="row justify-content-center">
                                                <div class="col-4 text-align-end text-danger">PAK BIL:</div>
                                                <div class="col-4 px-2">
                                                    <span class="text-danger">{{ trip.carPackingTime }}</span>
                                                    <span class="ml-5">AFGANG:&nbsp;{{ trip.departureTime }}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="col-6">
                                            <div class="row justify-content-center">
                                                <h1 class="route-title">{{ trip.routeLetter }}</h1>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr class="stop-row" v-for="order, index in trip.orders" :key="index">
                            <td>
                                <div class="row mb-4">
                                    <div class="col-12 px-0">
                                        <hr />
                                    </div>
                                    <div class="col-12">
                                        <div class="row ">
                                            <div class="col-1 route header">
                                                {{ getRouteName(trip.routeLetter, index) }}&nbsp;<i class="far fa-square"></i>
                                            </div>
                                            <div class="col-3 route header">
                                                BESTILLING
                                            </div>
                                            <div class="col-3 route header">
                                                <span class="arrival">ANKOMST</span>&nbsp;{{ order.deliveryStart }}
                                            </div>
                                            <div class="col-3 route header">
                                                <span class="departure">AFGANG</span>&nbsp;{{ order.deliveryEnd }}
                                            </div>
                                            <div class="col-2 route header">
                                                <span>INTERVAL:</span><br>{{ order.deliveryDateTime }}
                                            </div>
                                        </div>
                                        <div v-if="order.isManualStop" class="row mt-4">
                                          <div class="col-6 single-line-block">{{ order.manualStopDescription }}</div>
                                          <div class="col-6 single-line-block">{{ order.manualStopAddress }}</div>
                                        </div>
                                        <div v-else class="row mt-4">
                                            <div class="col-1">
                                                <span
                                                    :class="`text-uppercase font-weight-bold ${order.delivery_type === 'cold' ? 'text-primary' : 'text-danger'}`">
                                                    {{ getDeliveryTypeName(order.delivery_type) }}
                                                </span>
                                            </div>
                                            <div class="col-3">
                                                <div class="single-line-block">
                                                    {{ order.total_quantity }} x {{ order.product.name }}
                                                </div>
                                                <div v-for="(extra, index) in order.order_products" :key="index">
                                                    <span class="extra-product-line">{{ extra.quantity }} x {{ extra.product ?
                                                        extra.product.name : '' }}</span>
                                                </div>
                                            </div>
                                            <div class="col-3 align-self-start">
                                                <div>
                                                    {{ order.delivery_address.address_1 }}
                                                </div>
                                                <div>
                                                    {{ order.delivery_address.zipcode }}&nbsp;{{ order.delivery_address.city }}
                                                </div>
                                                <div v-if="order.delivery_address.note" class="single-line">
                                                    {{ order.delivery_address.note }}
                                                </div>
                                            </div>
                                            <div class="col-3 align-self-start">
                                                <div class="single-line-block"><a :href="order.url" class="print-link"
                                                        target="_blank">#{{ order.reference_code }}</a></div>
                                                <div class="single-line-block">{{ order.user.name }}</div>
                                                <div class="single-line-block">{{ order.delivery_address.phone }}</div>
                                            </div>
                                            <div class="col-2 bolder-text">
                                                <div class="d-flex justify-content-between">
                                                    <div>KASSER:</div>
                                                    <div class="ml-1 w-100 bottom-border"></div>
                                                </div>
                                                <div class="d-flex justify-content-between">
                                                    <div>BELØB:&nbsp;</div>
                                                    <div class="ml-1 w-100 bottom-border"></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr class="stop-row">
                            <td>
                                <div class="row justify-content-between my-2">
                                    <div class="col-10">
                                        <span class="h3 text-bolder text-uppercase">tilbage (tøm bil): {{ trip.tripEmptyCar.time
                                            }}</span>
                                    </div>
                                    <div class="col-2">
                                        <span class="h3 text-bolder text-uppercase">{{ trip.tripEmptyCar.minutes }} MIN.</span>
                                    </div>
                                </div>
                                <div class="row justify-content-between my-2">
                                    <div class="col-12 text-danger">
                                        <span class="h3 text-bolder text-uppercase">rute slut: {{ trip.tripEnd }}</span>
                                    </div>
                                </div>
                                <div v-if="trip.tripBreak.minutes > 0" class="row justify-content-between my-2">
                                    <div class="col-10">
                                        <span class="h3 text-bolder text-uppercase">pause slut: {{ trip.tripBreak.time }}</span>
                                    </div>
                                    <div class="col-2">
                                        <span class="h3 text-bolder text-uppercase">{{ trip.tripBreak.minutes }} MIN.</span>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </print-container>
    </div>
</template>

<script>
import axios from 'axios';
import * as moment from 'moment';
import { ref } from 'vue';

import SpinnerOverlay from '@/components/SpinnerOverlay.vue';
import { Order } from '@/models/order';
import PrintContainer from '@/print/PrintContainer.vue';
import { useRouteDataStore } from '@/stores/route-data';

export default {
  components: { PrintContainer, SpinnerOverlay },
  props: ['date', 'deliveryZoneId', 'vehicles', 'drivers'],
  setup(props) {
    const loading = ref(true);
    const routeDataStore = useRouteDataStore();
    const trips = ref([]);

    moment.locale('da');

    const deliveryDate = moment(props.date);

    const tripNames = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
    const getTrips = (orders) => {
      const ordersByTrips = { none: [], A: [], B: [], C: [], D: [], E: [], F: [], G: [] };
      for (let i = 0; i < orders.length; i++) {
        const order = orders[i];
        if (order.route_code) {
          ordersByTrips[order.route_code].push(order);
        } else {
          ordersByTrips.none.push(order);
        }
      }
      for (let i = 0; i < tripNames.length; i++) {
        ordersByTrips[tripNames[i]].sort((a, b) => {
          return a.tour_code - b.tour_code;
        });
      }
      ordersByTrips.none.sort((a, b) => {
        return a.deliveryIntervalStart.localeCompare(b.deliveryIntervalStart);
      });
      return ordersByTrips;
    };

    const getCarPackingTime = (firstOrder) => {
      const id = `${firstOrder.route_code}_start`;
      const duration = Object.hasOwn(routeDataStore.routeData.service_durations, id) ? routeDataStore.routeData.service_durations[id] : null;
      if (firstOrder.driveStart && duration !== null) {
        return moment(firstOrder.driveStart, 'HH:mm').subtract(duration, 'minutes').format('HH:mm');
      } else {
        return null;
      }
    };

    const getEmptyCarTime = (lastOrder) => {
      const emptyCarDurationId = `${lastOrder.route_code}_end`;
      const end = routeDataStore.routeData && Object.hasOwn(routeDataStore.routeData.drive_durations, emptyCarDurationId) ? routeDataStore.routeData.drive_durations[emptyCarDurationId] : null;

      if (lastOrder.deliveryEnd && end) {
        return moment(lastOrder.deliveryEnd, "HH:mm").add(end, 'minutes').format('HH:mm');
      } else {
        return null;
      }
    };
    const getEmptyCarDuration = (lastOrder) => {
      const emptyCarDurationId = `${lastOrder.route_code}_end`;
      return Object.hasOwn(routeDataStore.routeData.service_durations, emptyCarDurationId) ? routeDataStore.routeData.service_durations[emptyCarDurationId] : null;
    };

    const getRouteEndTime = (lastOrder) => {
      const emptyCarDuration = getEmptyCarTime(lastOrder);
      const emptyCarMinutes = getEmptyCarDuration(lastOrder);

      if (emptyCarDuration && emptyCarMinutes) {
        return moment(emptyCarDuration, 'HH:mm').add(emptyCarMinutes, 'minutes').format('HH:mm');
      } else {
        return null;
      }
    };

    const getBreakDuration = (lastOrder) => {
      const id = `${lastOrder.route_code}_break`;
      return Object.hasOwn(routeDataStore.routeData.service_durations, id) ? routeDataStore.routeData.service_durations[id] : null;
    };

    const getBreakTime = (lastOrder) => {
      const breakMinutes = getBreakDuration(lastOrder);
      const routeEndTime = getRouteEndTime(lastOrder);

      if (breakMinutes && routeEndTime) {
        return moment(routeEndTime, 'HH:mm').add(breakMinutes, 'minutes').format('HH:mm');
      } else {
        return null;
      }
    };

    axios
      .get(`/order_management/orders/orders_data?type=deliveries&date=${props.date}&delivery_zone=${props.deliveryZoneId}`)
      .then(response => {
        if (response.data.route_data === null) {
          loading.value = false;
          alert('Der er ikke gemt noget kørselsdata for denne dato, og kørselslisten kan derfor ikke genereres.');
          return;
        }

        const allOrders = response.data.orders;
        const manualStops = response.data.route_data ? response.data.route_data.manual_stops ?? [] : [];
        for (let i = 0; i < manualStops.length; i++) {
          const stop = manualStops[i];
          allOrders.push({
            id: stop.id,
            isManualStop: true,
            delivery_zone_id: stop.delivery_zone_id,
            route_code: stop.route_code,
            tour_code: stop.tour_code,
            expected_delivery_time: stop.expected_delivery_time,
            manualStopAddress: stop.manualStopAddress,
            manualStopDescription: stop.manualStopDescription,
            vehicle_id: stop.vehicle_id,
            driver_id: stop.driver_id,
          });
        }

        const orders = allOrders.map((order) => {
          return Order.createReactive(order);
        });
        routeDataStore.setRouteData(response.data.route_data);

        //loop orders and add warm orders to warmTrips
        const tripOrders = getTrips(orders);

        for (const tripName of tripNames) {
          const orders = tripOrders[tripName].filter(order => order.route_code === tripName);

          if (orders.length > 0) {
            const firstOrder = tripOrders[tripName][0];
            const lastOrder = tripOrders[tripName][orders.length - 1];

            const tripEmptyCar = {
              time: getEmptyCarTime(lastOrder),
              minutes: getEmptyCarDuration(lastOrder)
            };
            const tripEnd = getRouteEndTime(lastOrder);
            const tripBreak = {
              time: getBreakTime(lastOrder),
              minutes: getBreakDuration(lastOrder),
            };

            trips.value.push({
              departureTime: response.data.route_data === null ? null : firstOrder.driveStart,
              driverName: firstOrder.driver_id ? props.drivers.find(driver => driver.id === firstOrder.driver_id).name : '-',
              vehicleName: firstOrder.vehicle_id ? props.vehicles.find(vehicle => vehicle.id === firstOrder.vehicle_id).title : '',
              carPackingTime: getCarPackingTime(firstOrder),
              routeLetter: tripName,
              tripEmptyCar,
              tripEnd,
              tripBreak,
              orders: orders,
            });
          }
        }
      }).finally(() => {
        loading.value = false;
        if (shouldOpenPrintDialog() && routeDataStore.routeData !== null) {
          setTimeout(window.print, 250);
        }
      });
    
    const shouldOpenPrintDialog = () => {
      const urlParams = new URLSearchParams(window.location.search);
      return !urlParams.has('no_print') || urlParams.get('no_print') !== 'true';
    }

    return {
      loading,
      deliveryDate,
      trips,

      // methods
      getRouteName: (routeLetter, index) => `${routeLetter}${index + 1}`,
      getDeliveryTypeName: (delivery_type) => delivery_type === 'cold' ? 'kold' : 'varm'
    }
  }
}
</script>
<style lang="scss">
$base-color: #000000;
$title-color: #FFFFFF;
$title-background-color: #B45F06;
$header-background-color: #F2F2F2;

$focus-color: #FF0000;
$cold-color: #1154CC;
$warm-color: #990000;
$break-color: #D27B09;
$arrival-color: #6CA74E;
$departure-color: #992167;
$pitstop-color: #FF00FF;

#print-delivery-component {
  color: $base-color;

  // How wide the print sits on the page. Bootstrap's .container caps its width at a
  // breakpoint max-width and adds $grid-gutter-width/2 (15px) of horizontal padding.
  // Neither earns its keep in print, where the paper margin already supplies the
  // whitespace — so widen the printable area and let the page margin do that job.
  // Turn this knob to trade content width against side margin.
  .container {
    max-width: none;
    padding-left: 0;
    padding-right: 0;
  }

  .trip-table {
    width: 100%;
    border-collapse: collapse;

    // The table is a single column; it exists only so the browser will repeat the
    // banner in thead and keep a stop together. It must not impose any styling of
    // its own on the Bootstrap grid inside each cell.
    > thead > tr > th,
    > tbody > tr > td {
      padding: 0;
      font-weight: inherit;
      text-align: inherit;
      vertical-align: top;
    }
  }

  // Løsningsforslag 1: a stop's header row and its detail row stay on one page
  // rather than splitting across the break.
  .stop-row {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  // Centring and white text used to come from PrintContainer's .print-header, which
  // this view no longer uses.
  .title-header {
    color: $title-color;
    text-align: center;
    padding: 10px;
    margin: 0;
  }
}

.bolder-text {
  font-weight: bolder;
}

.bottom-border {
  border-bottom: 1px solid $base-color !important;
}

hr {
  border-top: 2px solid $base-color !important;
  margin: 0 !important;
}

.title {
  color: $title-color;
}

.title-header {
  background-color: $title-background-color;
}

.delivery-header {
  font-weight: bold;
  font-size: larger;
  // Holds the block to three lines whatever the driver name is: the longest active
  // name is 20 characters and would otherwise wrap into a fourth line.
  white-space: nowrap;

  >p {
    margin: 0;
  }
}

.route {
  &.header {
    font-weight: bolder;
    background-color: $header-background-color;
  }

  >.arrival {
    color: $arrival-color;
  }

  >.departure {
    color: $departure-color;
  }
}

.focus {
  color: $focus-color;
}

h1.route-title {
  margin: 0;
  @extend .bolder-text;
}

.text-align-center {
  text-align: center;
}

.text-align-end {
  text-align: end;
}
</style>
<style lang="scss" scoped>
.row {
  margin-left: -8px;
  margin-right: -8px;
}
@for $i from 1 through 12 {
    .col-#{$i} {
      padding-left: 8px;
      padding-right: 8px;
    }
}

</style>